<template>
  <div>
    <HeroSecondary
      v-if="hero"
      v-bind="hero"
    />
    <!-- eslint-disable vue/no-unused-vars -->
    <div class="team-members">
      <ul
        v-if="columns"
        class="team-members__list-left"
      >
        <li
          v-for="member in columns[0]"
          :key="member.slug"
          class="team-members__list-item"
        >
          <TeamMember v-bind="member"/>
        </li>
      </ul>
      <ul
        v-if="columns"
        class="team-members__list-right"
      >
        <li
          v-for="member in columns[1]"
          :key="member.slug"
          class="team-members__list-item"
        >
          <TeamMember v-bind="member"/>
        </li>
      </ul>
    </div>
    <div class="team-advisors__container">
      <div class="team-advisors__heading-container">
        <h2 class="team-advisors__heading">UNLTD Advisory Board</h2>
        <!-- eslint-disable-next-line max-len -->
        <p class="team-advisors__subheading">Voices mean something. Perspective means something. When the UNLTD Team has a question, they want to call on the best. So we put together an Advisory Board. They will help us disrupt the industry.</p>
      </div>
      <div class="team-advisors">
        <ul
          v-if="advisors"
          class="team-advisors__list"
        >
          <li
            v-for="({ name, role, title, organization }) in advisors"
            :key="name"
            class="team-advisors__list-item"
          >
            <div
              class="team-advisor__role"
              v-text="role"
            />
            <div
              class="team-advisor__name"
              v-text="name"
            />
            <div
              class="team-advisor__title"
              v-text="title"
            />
            <div
              class="team-advisor__organization"
              v-text="organization"
            />
          </li>
        </ul>
      </div>
    </div>
    <div class="team-partners">
      <div class="team-partners__heading-container">
        <h2 class="team-partners__heading">UNLTD Partners</h2>
        <!-- eslint-disable max-len -->
        <p class="team-partners__subheading">UNLTD Instructors need the best tools to do their job. Our Instructors use and recommend the following, because we believe in them.</p>
      </div>
      <ul
        v-if="partners"
        class="team-partners__list"
      >
        <li
          v-for="({ name, logo, link }) in partners"
          :key="name"
          class="team-partners__list-item"
        >
          <a
            :href="link"
            class="team-partner__link"
            target="_blank"
            rel="noopener noreferrer"
          >
            <BaseImage
              v-if="logo"
              :id="logo"
              class="team-partners__image"
            />
            <span class="team-partners__purpose">{{ name }}</span>
          </a>
        </li>
      </ul>
    </div>
    <!-- eslint-enable vue/no-unused-vars -->
  </div>
</template>

<script>
  import Member from '@/api/members';
  import Advisor from '@/api/advisors';
  import Partner from '@/api/partners';
  import { cleanMember, cleanAdvisor, cleanPartner } from '@/util/cleaners';

  import EmailIcon from './EmailIcon';
  import TeamMember from './TeamMember';
  import HeroSecondary from './HeroSecondary';
  import DisclosureButton from './DisclosureButton';
  import DisclosureContainer from './DisclosureContainer';

  export default {
    components: {
      EmailIcon,
      TeamMember,
      HeroSecondary,
      DisclosureButton,
      DisclosureContainer,
    },
    props: {
      hero: {
        type: Object,
        default: null,
      },
    },
    data() {
      return {
        members: undefined,
        advisors: undefined,
        partners: undefined,
      };
    },
    computed: {
      columns() {
        const { members } = this;

        if (!members) {
          return null;
        }

        const index = Math.floor(members.length / 2);

        return [members.slice(0, index), members.slice(index)];
      },
    },
    cleanProps({ acf }) {
      return acf;
    },
    async created() {
      this.members = (await Member.all()).map(cleanMember);
      this.advisors = (await Advisor.all()).map(cleanAdvisor);
      this.partners = (await Partner.all()).map(cleanPartner);
    },
  };
</script>

<style lang="scss">
  .team-members {
    @include container;
    position: relative;
    display: flex;
    flex-direction: column;
    z-index: 1;
    padding-bottom: spacing(base-epsilon);

    @include breakpoint(phablet) {
      flex-direction: row;
      padding-bottom: spacing(base-eta);
    }
  }

  .team-members__list-left,
  .team-members__list-right {
    @include column;
    width: 100%;

    @include breakpoint(phablet) {
      width: percentage(5 / 12);
      margin-left: percentage(1 / 12);
    }
  }

  .team-members__list-left {
    margin-top: spacing(base-zeta);

    &:first-child {
      margin-top: spacing(base-delta);
    }

    @include breakpoint(phablet) {
      &:first-child {
        margin-top: spacing(base-zeta);
      }
    }
  }

  .team-members__list-right {
    margin-top: spacing(base-zeta);

    @include breakpoint(phablet) {
      margin-top: - spacing(base-zeta);
    }
  }

  .team-members__list-item {
    margin-top: spacing(base-zeta);

    &:first-child {
      margin-top: 0;
    }
  }

  .team-members__list {
    position: relative;
    z-index: 1;
  }

  .team-member__image-container {
    position: relative;
    width: 100%;
    padding-top: percentage(9 / 16);

    @include breakpoint(phablet) {
      padding-top: percentage(3 / 4);
    }
  }

  .team-member__image {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .team-member__name {
    margin-top: spacing(base-alpha);
    font-size: 1.6rem;
    font-weight: font-weight(bold);
    color: color(slate);

    @include breakpoint(phablet) {
      font-size: 2.7rem;
    }
  }

  .team-member__role {
    margin-top: spacing(base-alpha);
    font-size: 1.2rem;
    font-size: font-family(sans-serif);
    font-weight: font-weight(bold);
    text-transform: uppercase;
    color: color(light-grey);

    @include breakpoint(phablet) {
      font-size: 1.4rem;
    }
  }

  .team-member__bio {
    margin-top: spacing(base-alpha);
    font-size: 1.5rem;
    font-size: font-family(sans-serif);
    color: color(slate);
    line-height: 1.5;

    @include breakpoint(phablet) {
      font-size: 2rem;
    }
  }

  .team-member__toggle-bio {
    padding-top: spacing(base-alpha);
    padding-bottom: spacing(base-alpha);
    display: flex;
    align-items: center;
    color: color(bronze);
    transition-property: color;
    transition-duration: duration(fast);
    transition-timing-function: timing-function(standard);

    &:focus,
    &:hover {
      color: color(red);
      outline-style: none;
    }
  }

  .access-hidden {
    @include access-hidden;
  }

  .team-advisors__container {
    padding-top: spacing(base-delta);
    padding-bottom: spacing(base-zeta);
    background-color: color(slate);

    @include breakpoint(phablet) {
      padding-top: spacing(base-zeta);
      padding-bottom: spacing(base-eta);
    }
  }

  .team-advisors__heading-container{
    @include container;
    width: 100%;
    margin-right: auto;
    margin-left: auto;
    color: color(white);
    text-align: center;

    @include breakpoint(phablet) {
      width: percentage(6.9 / 12);
    }
  }

  .team-advisors__heading {
    font-family: font-family(serif);
    font-size: 1.6rem;
    font-weight: font-weight(bold);
    color: color(bronze);

    @include breakpoint(phablet) {
      font-size: 2.6rem;
    }
  }

  .team-advisors__subheading {
    margin-top: spacing(base-delta);
    font-size: 1.5rem;
    line-height: 1.5;

    @include breakpoint(phablet) {
      margin-top: spacing(base-epsilon);
      font-size: 2rem;
    }
  }

  .team-advisors {
    @include container;
    color: color(white);
  }

  .team-advisors__list {
      @include column;
      display: flex;
      justify-content: space-between;
      flex-wrap: wrap;
      width: 100%;
  }

  .team-advisors__list-item {
      display: inline-block;
      width: 100%;
      margin-top: spacing(base-epsilon);
      text-align: center;

      @include breakpoint(phablet) {
        width: 50%;
        margin-top: spacing(base-zeta);
      }
  }

  .team-advisor__role,
  .team-advisor__name,
  .team-advisor__title {
    font-family: font-family(sans-serif);
  }

  .team-advisor__role,
  .team-advisor__name {
    text-transform: uppercase;
  }

  .team-advisor__name,
  .team-advisor__title {
    color: color(white);
  }

  .team-advisor__role {
    font-size: 1.2rem;
    color: color(grey);

    @include breakpoint(phablet) {
      font-size: 1.4rem;
    }
  }

  .team-advisor__name,
  .team-advisor__title {
    font-size: 1.4rem;

    @include breakpoint(phablet) {
      font-size: 2rem;
    }
  }

  .team-advisor__name {
    margin-top: spacing(base-delta);
  }

  .team-advisor__title {
    margin-top: spacing(base-beta);
  }

  .team-advisor__organization {
    margin-top: spacing(base-delta);
    font-size: 2.6rem;
    font-family: font-family(serif);
    color: color(bronze);

    @include breakpoint(phablet) {
      font-size: 3.3rem;
    }
  }

  .team-partners {
    @include container;
    padding-top: spacing(base-delta);
    padding-bottom: spacing(base-zeta);

    @include breakpoint(phablet) {
      padding-top: spacing(base-zeta);
      padding-bottom: spacing(base-eta);
    }
  }

  .team-partners__heading,
  .team-partners__subheading {
    @include column;
    font-family: font-family(sans-serif);
    line-height: 1.5;
    text-align: center;
  }

  .team-partners__heading {
    font-weight: font-weight(bold);
    font-size: 1.6rem;
    color: color(red);

    @include breakpoint(phablet) {
      font-size: 2.6rem;
    }
  }

  .team-partners__subheading {
    width: 100%;
    margin-top: spacing(base-delta);
    margin-right: auto;
    margin-left: auto;
    font-size: 1.5rem;
    color: color(bronze);

    @include breakpoint(phablet) {
      margin-top: spacing(base-epsilon);
      width: percentage(8/12);
      font-size: 2rem;
    }
  }

  .team-partners__purpose {
    margin-top: spacing(base-delta);
    font-size: 1.4rem;
    font-family: font-family(sans-serif);
    font-weight: font-weight(bold);
    color: color(grey);
  }

  .team-partners__list {
    @include column;
    margin-top: spacing(base-delta);

    @include breakpoint(phablet) {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      margin-top: spacing(base-epsilon);
    }
  }

  .team-partners__list-item {
    display: inline-block;
    width: 100%;
    margin: 5px 0;

    @include breakpoint(phablet) {
      width: calc(50% - 10px);
      margin: 0 5px;
    }

    @include breakpoint(tablet) {
      width: calc(20% - 10px);
      margin: 0 5px;
    }
  }

  .team-partner__link {
    display: inline-flex;
    width: 100%;
    flex-direction: column;
    align-items: center;
    padding-top: spacing(base-epsilon);
    padding-right: spacing(base-delta);
    padding-bottom: spacing(base-epsilon);
    padding-left: spacing(base-delta);
    border-radius: 8px;
    transition-property: background-color;
    transition-timing-function: timing-function(accelerate);
    transition-duration: duration(fast);

    &:hover,
    &:focus {
      background-color: rgba(0, 0, 0, 0.1);
      transition-timing-function: timing-function(decelerate);
    }
  }

  .team-member__email {
    position: absolute;
    bottom: 15px;
    right: 15px;
    display: inline-block;
    width: 40px;
    height: 40px;
    padding: 5px;
    background-color: color(bronze);
    border-radius: 50%;
    transition-duration: duration(fast);
    transition-timing-function: timing-function(standard);
    transition-property: background-color;

    &:hover,
    &:focus {
      background-color: color(red);
      outline-style: none;
    }

    @include breakpoint(tablet) {
      bottom: 25px;
      right: 25px;
      width: 45px;
      height: 45px;
    }
  }

  .team-member__email-icon {
    fill: white;
  }

  .team-member__button-indicator {
    margin-left: spacing(base-alpha);

    .team-member__toggle-bio & .line {
      transition-property: border-color;
      transition-duration: duration(fast);
      transition-timing-function: timing-function(standard);
    }

    .team-member__toggle-bio:focus & .line,
    .team-member__toggle-bio:hover & .line {
      border-color: color(red);
    }
  }

  .team-partners__image {
    height: 100px;
  }
</style>
